import * as React from 'react';
import { useParams } from 'react-router-dom';

import { usePublicOrganizerProfile } from '@/domains/organizer-profile/api';
import { PublicOrganizerProfilePage } from '@/domains/organizer-profile/public-profile-page';

import { usePublishedPage } from './api';
import { CmsPage, CmsPageSkeleton } from './cms-page';
import { NotFoundPage } from './not-found-page';

/**
 * What answers a bare `/{slug}` — the organizer profile first, a CMS page only if nothing else
 * claims the address (`CMS-009` AC-4).
 *
 * ## The precedence, and why it points this way
 *
 * The public organizer profile and a marketing page occupy the same shape of URL. The client
 * decided on 2026-08-22 that **everything else resolves first and the CMS is the fallback**, and
 * that is the safe direction of the two: no amount of content editing can take an organizer's
 * profile off the air, and the losing side fails as a page that does not appear rather than as an
 * organizer who vanishes.
 *
 * It also means no reserved-slug list is needed for the site to be *correct* — only for it to be
 * kind, which is what the editor's collision warning is for.
 *
 * ## Why a resolver rather than route ordering
 *
 * React Router matches on the shape of a path, not on whether data exists behind it, so two
 * routes at `/:slug` cannot take turns. The precedence has to be expressed as a lookup order, and
 * this is the one place it is expressed.
 *
 * The organizer lookup runs here rather than inside the profile page so that the fallback is a
 * decision made from data rather than a callback fired during someone else's render. The profile
 * page then reads the same query from cache, so asking twice costs one request.
 */
export function SlugRoutePage(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  const organizer = usePublicOrganizerProfile(slug ?? '');

  // Only asked once the organizer lookup has actually failed — not merely while it is in flight,
  // which would put a request on the wire for every organizer profile anyone visits.
  const organizerMissing = !organizer.isLoading && (organizer.isError || organizer.data === undefined);
  const page = usePublishedPage(slug, { enabled: organizerMissing });

  if (organizer.isLoading) return <CmsPageSkeleton />;
  if (!organizerMissing) return <PublicOrganizerProfilePage />;

  if (page.isLoading) return <CmsPageSkeleton />;
  if (page.data !== undefined) return <CmsPage page={page.data} />;

  // Nothing claims this address. The site's own 404 rather than the organizer profile's, because
  // "there is no organizer here" is a specific claim and this is the general case — a visitor who
  // mistyped `/pricng` should not be told H2HSpark has no organizer by that name.
  return <NotFoundPage />;
}
